<script>
/**
 * Modal de anúncio do NOVO ERP (campanha de migração 2026).
 *
 * Comportamento:
 *  - Abre uma vez por carga (após o app logar), com um pequeno atraso pra não
 *    competir com a tela de "aquecendo a aplicação".
 *  - O ÚNICO jeito de não ver mais é marcar "Não quero mais ver este aviso":
 *    isso grava um cookie de longa duração (365 dias) e nunca mais mostra.
 *  - Fechar (X / clicar fora / CTA) SEM marcar NÃO grava cookie — o aviso
 *    volta a aparecer no próximo carregamento (F5 / novo login).
 *
 * Identidade visual baseada no site novo (suporteleiloes.com.br):
 *  ink #050816 / blue #2563EB / amber #F59E0B, cantos arredondados, glow.
 */

const COOKIE_NAME = 'sl_aviso_novo_erp'
const NEVER_DAYS = 365     // marcou "não mostrar mais" → some de vez

// Destino do CTA: HOME do site novo com ?migracao=<domínio do ERP atual>.
// A home detecta o parâmetro e abre a área de contratação no topo, empurrando
// o site pra baixo. O domínio identifica de qual cliente veio.
const NOVO_ERP_BASE = 'https://v2.suporteleiloes.com.br/'
function buildMigracaoUrl() {
  const dominio = (typeof window !== 'undefined' && window.location && window.location.hostname) || ''
  const params = new URLSearchParams()
  params.set('migracao', dominio || '1')
  return `${NOVO_ERP_BASE}?${params.toString()}`
}

// URL do vídeo de apresentação (deixe vazio até gravar). Aceita link normal do
// YouTube/Vimeo (é convertido pra embed) ou um .mp4 direto.
const VIDEO_URL = 'https://vimeo.com/1201827234'

function getCookie(name) {
  const match = document.cookie.match(new RegExp('(?:^|; )' + name.replace(/([.$?*|{}()[\]\\/+^])/g, '\\$1') + '=([^;]*)'))
  return match ? decodeURIComponent(match[1]) : null
}
function setCookie(name, value, days) {
  const d = new Date()
  d.setTime(d.getTime() + days * 24 * 60 * 60 * 1000)
  document.cookie = `${name}=${encodeURIComponent(value)}; expires=${d.toUTCString()}; path=/; SameSite=Lax`
}

export default {
  name: 'ModalNovoErp',
  data() {
    return {
      opened: false,
      naoMostrarMais: false,
      videoUrl: VIDEO_URL,
    }
  },
  computed: {
    // Converte link do YouTube/Vimeo em URL de embed; .mp4 retorna como está.
    videoEmbed() {
      const url = (this.videoUrl || '').trim()
      if (!url) return null
      let m
      if ((m = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([\w-]{6,})/))) {
        return { type: 'iframe', src: `https://www.youtube.com/embed/${m[1]}?rel=0&modestbranding=1` }
      }
      if ((m = url.match(/vimeo\.com\/(?:video\/)?(\d+)/))) {
        // badge=0 (sem selo Vimeo), autopause=0, dnt=1 (do-not-track / sem cookies).
        // Preserva o hash de vídeo não-listado (?h=xxxx) se vier na URL.
        const h = (url.match(/[?&]h=([\w]+)/) || [])[1]
        const params = `badge=0&autopause=0&dnt=1${h ? `&h=${h}` : ''}`
        return { type: 'iframe', src: `https://player.vimeo.com/video/${m[1]}?${params}` }
      }
      if (/\.(mp4|webm|ogg)(\?.*)?$/i.test(url)) {
        return { type: 'video', src: url }
      }
      return { type: 'iframe', src: url }
    },
  },
  mounted() {
    // Escapa da árvore de componentes pra ficar acima de tudo (igual Autorizador).
    document.body.append(this.$el)
    if (getCookie(COOKIE_NAME)) return
    // Pequeno atraso: deixa a aplicação terminar de carregar antes de aparecer.
    this.$openTimer = setTimeout(() => {
      this.opened = true
      this.track('novo-erp-modal/abrir')
    }, 1400)
  },
  beforeDestroy() {
    this.$openTimer && clearTimeout(this.$openTimer)
    document.body.contains(this.$el) && document.body.removeChild(this.$el)
  },
  methods: {
    track(evt) {
      try {
        const session = this.$uloc && this.$uloc.auth && this.$uloc.auth.session
        const userId = session && session.user && session.user.id
        typeof SLEventClient === 'function' && SLEventClient('click', 'erp', evt, NOVO_ERP_BASE, userId)
      } catch (e) { /* noop */ }
    },
    // Só grava cookie (e some de vez) se o usuário marcou o checkbox.
    persistDismiss() {
      if (this.naoMostrarMais) {
        setCookie(COOKIE_NAME, 'never', NEVER_DAYS)
      }
    },
    fechar() {
      this.persistDismiss()
      this.opened = false
    },
    conhecer() {
      this.track('novo-erp-modal/cta')
      this.persistDismiss()
      window.open(buildMigracaoUrl(), '_blank', 'noopener')
      this.opened = false
    },
  },
}
</script>

<template>
  <div class="snerp" v-if="opened">
    <div class="snerp__overlay" @click="fechar"></div>

    <div class="snerp__card" role="dialog" aria-modal="true" aria-labelledby="snerp-title">
      <button class="snerp__x" type="button" aria-label="Fechar" @click="fechar">
        <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
      </button>

      <div class="snerp__glow" aria-hidden="true"></div>

      <div class="snerp__body">
        <span class="snerp__badge">
          <span class="snerp__badge-dot"></span> Novo ERP · 2026
        </span>

        <h2 id="snerp-title" class="snerp__title">
          Nosso novo ERP, depois de 2 anos de desenvolvimento, <span class="snerp__hl">chegou!</span> 🚀
        </h2>

        <p class="snerp__text">
          Juntamos <strong>10 anos de experiência</strong> em erros e acertos do nosso antigo ERP e
          reescrevemos um sistema <strong>do zero</strong> para solucionar e evoluir tudo que sempre
          imaginamos, aproveitando o que temos de mais moderno hoje: <strong class="snerp__ia">IA</strong>.
        </p>
        <p class="snerp__text">
          A migração será planejada <strong>cliente por cliente a partir de 20 de julho de 2026</strong>,
          mas você pode antecipar e ser um dos primeiros a utilizar todo o poder da nossa tecnologia.
        </p>

        <!-- Vídeo de apresentação -->
        <div class="snerp__video">
          <template v-if="videoEmbed">
            <iframe
              v-if="videoEmbed.type === 'iframe'"
              :src="videoEmbed.src"
              frameborder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowfullscreen
            ></iframe>
            <video v-else :src="videoEmbed.src" controls playsinline></video>
          </template>
          <div v-else class="snerp__video-ph">
            <div class="snerp__play" aria-hidden="true">
              <svg viewBox="0 0 24 24" width="26" height="26" fill="currentColor"><path d="M8 5v14l11-7z"/></svg>
            </div>
            <span class="snerp__video-label">Vídeo de apresentação · em breve</span>
          </div>
        </div>

        <button class="snerp__cta" type="button" @click="conhecer">
          Quero conhecer para migrar
          <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
        </button>

        <label class="snerp__dismiss">
          <input type="checkbox" v-model="naoMostrarMais" />
          <span>Não quero mais ver este aviso</span>
        </label>
      </div>
    </div>
  </div>
</template>

<style scoped>
.snerp {
  position: fixed;
  inset: 0;
  z-index: 12050;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
}
.snerp__overlay {
  position: absolute;
  inset: 0;
  background: rgba(5, 8, 22, .72);
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
  animation: snerp-fade .25s ease;
}
.snerp__card {
  position: relative;
  width: 100%;
  max-width: 620px;
  max-height: calc(100vh - 40px);
  overflow-y: auto;
  border-radius: 24px;
  border: 1px solid rgba(138, 180, 251, .18);
  background:
    radial-gradient(ellipse 80% 50% at 50% -10%, rgba(37, 99, 235, .35), transparent 60%),
    radial-gradient(ellipse 60% 50% at 85% 15%, rgba(245, 158, 11, .14), transparent 60%),
    linear-gradient(180deg, #0B1224 0%, #050816 100%);
  box-shadow:
    0 30px 80px -20px rgba(0, 0, 0, .7),
    0 0 0 1px rgba(255, 255, 255, .03) inset;
  animation: snerp-pop .32s cubic-bezier(.22, 1, .36, 1);
}
.snerp__glow {
  position: absolute;
  top: -1px; left: 10%; right: 10%;
  height: 1px;
  background: linear-gradient(90deg, transparent, rgba(138, 180, 251, .7), transparent);
}
.snerp__x {
  position: absolute;
  top: 14px; right: 14px;
  width: 34px; height: 34px;
  display: inline-flex; align-items: center; justify-content: center;
  border: none; border-radius: 50%;
  background: rgba(255, 255, 255, .06);
  color: rgba(255, 255, 255, .65);
  cursor: pointer;
  transition: background .15s, color .15s;
  z-index: 2;
}
.snerp__x:hover { background: rgba(255, 255, 255, .12); color: #fff; }

.snerp__body {
  padding: 38px clamp(24px, 5vw, 44px) 28px;
}
.snerp__badge {
  display: inline-flex; align-items: center; gap: 7px;
  font-size: 12px; font-weight: 700; letter-spacing: .06em; text-transform: uppercase;
  color: #8AB4FB;
  background: rgba(37, 99, 235, .14);
  border: 1px solid rgba(124, 168, 247, .28);
  border-radius: 999px;
  padding: 5px 13px;
  margin-bottom: 18px;
}
.snerp__badge-dot {
  width: 7px; height: 7px; border-radius: 50%;
  background: #22c55e;
  box-shadow: 0 0 0 3px rgba(34, 197, 94, .22);
}
.snerp__title {
  font-size: clamp(23px, 3.4vw, 31px);
  line-height: 1.18;
  font-weight: 800;
  color: #fff;
  margin: 0 0 16px;
  letter-spacing: -.01em;
}
.snerp__hl {
  background: linear-gradient(135deg, #5C8DF6, #8AB4FB);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
}
.snerp__text {
  font-size: 15px;
  line-height: 1.62;
  color: rgba(226, 232, 240, .82);
  margin: 0 0 14px;
}
.snerp__text strong { color: #fff; font-weight: 600; }
.snerp__ia {
  color: #FBBF24 !important;
  font-weight: 700 !important;
}

.snerp__video {
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 9;
  margin: 22px 0 24px;
  border-radius: 14px;
  overflow: hidden;
  border: 1px solid rgba(138, 180, 251, .16);
  background: #060a18;
}
.snerp__video iframe,
.snerp__video video {
  position: absolute; inset: 0;
  width: 100%; height: 100%;
  border: none;
  display: block;
  object-fit: contain;
}
.snerp__video-ph {
  position: absolute; inset: 0;
  display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 14px;
  background:
    radial-gradient(ellipse 60% 60% at 50% 40%, rgba(37, 99, 235, .18), transparent 70%),
    #070b1a;
}
.snerp__play {
  width: 64px; height: 64px;
  display: inline-flex; align-items: center; justify-content: center;
  border-radius: 50%;
  background: linear-gradient(135deg, #2563EB, #1E40AF);
  color: #fff;
  padding-left: 4px;
  box-shadow: 0 10px 30px -8px rgba(37, 99, 235, .8), 0 0 0 8px rgba(37, 99, 235, .12);
}
.snerp__video-label {
  font-size: 13px; font-weight: 500;
  color: rgba(148, 163, 184, .9);
  letter-spacing: .02em;
}

.snerp__cta {
  width: 100%;
  display: inline-flex; align-items: center; justify-content: center; gap: 9px;
  height: 54px;
  border: none;
  border-radius: 13px;
  background: linear-gradient(135deg, #2563EB, #1D4FD8);
  color: #fff;
  font-family: inherit;
  font-size: 16.5px;
  font-weight: 700;
  cursor: pointer;
  box-shadow: 0 14px 34px -10px rgba(37, 99, 235, .85), 0 0 0 1px rgba(124, 168, 247, .2) inset;
  transition: transform .12s ease, box-shadow .2s ease, filter .2s ease;
}
.snerp__cta:hover {
  transform: translateY(-1px);
  filter: brightness(1.06);
  box-shadow: 0 18px 42px -10px rgba(37, 99, 235, .95), 0 0 0 1px rgba(124, 168, 247, .3) inset;
}
.snerp__cta:active { transform: translateY(0); }
.snerp__cta svg { transition: transform .15s ease; }
.snerp__cta:hover svg { transform: translateX(3px); }

.snerp__dismiss {
  display: flex; align-items: center; justify-content: center; gap: 8px;
  margin-top: 18px;
  font-size: 13.5px;
  color: rgba(148, 163, 184, .85);
  cursor: pointer;
  user-select: none;
}
.snerp__dismiss input {
  width: 16px; height: 16px;
  accent-color: #2563EB;
  cursor: pointer;
}
.snerp__dismiss:hover { color: rgba(203, 213, 225, .95); }

@keyframes snerp-fade { from { opacity: 0 } to { opacity: 1 } }
@keyframes snerp-pop {
  from { opacity: 0; transform: translateY(14px) scale(.97); }
  to   { opacity: 1; transform: translateY(0) scale(1); }
}

@media (max-width: 520px) {
  .snerp { padding: 0; align-items: flex-end; }
  .snerp__card {
    max-width: 100%;
    max-height: 92vh;
    border-radius: 22px 22px 0 0;
  }
  .snerp__body { padding: 34px 22px 24px; }
  .snerp__title { font-size: 22px; }
}
</style>
